import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaUser } from '@react-icons/all-files/fa/FaUser';

export interface UsuariosFilterProps {
  value?: any;
  onChange: (user: any | null) => void;
  label?: string;
  placeholder?: string;
  hasError?: boolean;
}

export default function UsuariosFilter({ value, onChange, label, placeholder, hasError }: UsuariosFilterProps) {
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setInputText(value ? `${value.nombre ?? ''} ${value.apellido ?? ''}`.trim() : '');
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setInputText(value ? `${value.nombre ?? ''} ${value.apellido ?? ''}`.trim() : '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value]);

  const doSearch = async (query: string) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    try {
      const res = await axios.get('api/usuarios', {
        params: { query, page: 0, size: 15, sort: 'nombre,asc' },
        signal: abortRef.current.signal,
      });
      const data = res.data;
      setResults(Array.isArray(data) ? data : (data.content ?? data.data ?? []));
      setOpen(true);
    } catch (err: any) {
      if (!axios.isCancel(err)) setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setInputText(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length >= 1) {
      debounceRef.current = setTimeout(() => doSearch(q), 200);
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  const handleSelect = (user: any) => {
    onChange(user);
    setInputText(`${user.nombre ?? ''} ${user.apellido ?? ''}`.trim());
    setResults([]);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setInputText('');
    setResults([]);
    setOpen(false);
  };

  const borderColor = hasError ? '#ef4444' : open ? '#6366f1' : '#cbd5e1';

  return (
    <div ref={containerRef} style={s.root}>
      {label && <label style={s.label}>{label}</label>}
      <div style={s.inputWrap}>
        <input
          type="text"
          value={inputText}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder ?? 'Buscar usuario…'}
          autoComplete="off"
          style={{ ...s.input, borderColor, background: hasError ? '#fff5f5' : '#fff' }}
        />
        <span style={s.adornment}>
          {loading
            ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', fontSize: 12, color: '#94a3b8' }} />
            : value
              ? <button type="button" onClick={handleClear} style={s.clearBtn}><FaTimes size={11} /></button>
              : null
          }
        </span>
      </div>

      {open && (results.length > 0 || (!loading && inputText.length >= 1)) && (
        <ul style={s.dropdown}>
          {results.length === 0
            ? <li style={s.noResults}>Sin resultados</li>
            : results.map(user => (
              <UserOption
                key={user.id}
                user={user}
                selected={value?.id === user.id}
                onSelect={handleSelect}
              />
            ))
          }
        </ul>
      )}
    </div>
  );
}

// ── User option row ────────────────────────────────────────────────────────────

function UserOption({ user, selected, onSelect }: { user: any; selected: boolean; onSelect: (u: any) => void }) {
  const habilitado = user.habilitado !== false;
  return (
    <li
      onMouseDown={() => onSelect(user)}
      style={{
        ...s.option,
        background: selected ? '#eef2ff' : habilitado ? '#fff' : '#f1f5f9',
        borderLeft: selected ? '3px solid #6366f1' : '3px solid transparent',
      }}
    >
      <UserAvatar user={user} habilitado={habilitado} />
      <div style={s.info}>
        <div style={s.nameRow}>
          <span style={{ ...s.name, color: habilitado ? '#0f172a' : '#94a3b8' }}>
            {user.nombre} {user.apellido}
          </span>
          {user.esContador && (
            <span style={s.contadorBadge}>
              <input type="checkbox" readOnly checked style={{ margin: 0, accentColor: '#6366f1', width: 12, height: 12 }} />
              Contador
            </span>
          )}
          {!habilitado && <span style={s.inactivoBadge}>Inactivo</span>}
        </div>
        {(user.usuario || user.email) && (
          <span style={s.sub}>{user.usuario ?? user.email}</span>
        )}
      </div>
    </li>
  );
}

// ── Avatar with fallback ───────────────────────────────────────────────────────

function UserAvatar({ user, habilitado }: { user: any; habilitado: boolean }) {
  const [error, setError] = useState(false);
  const initials = `${(user.nombre ?? '')[0] ?? ''}${(user.apellido ?? '')[0] ?? ''}`.toUpperCase();
  const bgColor = habilitado ? '#e0e7ff' : '#e2e8f0';
  const txtColor = habilitado ? '#6366f1' : '#94a3b8';

  if (error || !user.id) {
    return (
      <div style={{ ...s.avatar, background: bgColor, color: txtColor }}>
        {initials || <FaUser size={14} />}
      </div>
    );
  }

  return (
    <img
      src={`/public/usuario/${user.id}/image.png`}
      alt={`${user.nombre} ${user.apellido}`}
      style={{ ...s.avatar, objectFit: 'cover' }}
      onError={() => setError(true)}
    />
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    position: 'relative',
    width: '100%',
    fontFamily: 'inherit',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '7px 32px 7px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  adornment: {
    position: 'absolute',
    right: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  clearBtn: {
    all: 'unset',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: '#94a3b8',
    padding: 2,
    pointerEvents: 'auto',
    borderRadius: 3,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 1000,
    listStyle: 'none',
    margin: 0,
    padding: '4px 0',
    maxHeight: 300,
    overflowY: 'auto',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sub: {
    fontSize: 11,
    color: '#94a3b8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  contadorBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    fontWeight: 600,
    color: '#6366f1',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: 4,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
  },
  inactivoBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: '#94a3b8',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
  },
  noResults: {
    padding: '10px 14px',
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
};
